import { useEffect, useState } from 'react';
import { Container, Label } from '@/ui';
import { Button, Collapse, Input, Popconfirm, Typography } from 'antd';
import { Space, Box, ActionBox, NestedBox, StyledPanel } from '@/features/constructorCommon/styled';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useScoringCategory } from '@/features/constructorScoringCategory/hooks';
import { CommentSection } from '@/features/constructorScoringCategory/components/CommentSection';

export const ScoringCategory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [scoringCategory, setScoringCategory] = useState({});
  const { id } = useParams();
  const [showCreateNew, setShowCreateNew] = useState(false);

  const { data, isError, isUpdating, isDeleting, handleCreate, handleUpdate, handleDelete } =
    useScoringCategory(id);

  useEffect(() => {
    if (!data?.id) return;

    setScoringCategory(data);
  }, [data]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setScoringCategory({ ...scoringCategory, [name]: value });
  };

  const onChangeNumber = (e) => {
    const { name, value } = e.target;

    if (!Number(value) && value.length > 0) {
      return;
    }

    setScoringCategory({ ...scoringCategory, [name]: value });
  };

  const onDelete = () => {
    handleDelete(scoringCategory);
    navigate(`/constructorScoringCategory`);
  };

  const handleAction = async () => {
    if (scoringCategory.id) {
      handleUpdate(scoringCategory);
    } else {
      const result = await handleCreate(scoringCategory);

      if (result?.data?.id) {
        navigate(`/constructorScoringCategory/${result.data.id}`);
      }
    }
  };

  if (isError && !data) {
    return (
      <Container $paddingY={24}>
        <div>{t('Error')}</div>
      </Container>
    );
  }

  const isDisabled =
    scoringCategory.riskCategory === data?.riskCategory &&
    scoringCategory.name === data?.name &&
    scoringCategory.minPoints === data?.minPoints &&
    scoringCategory.maxPoints === data?.maxPoints &&
    scoringCategory.title === data?.title &&
    scoringCategory.name_RU === data?.name_RU &&
    scoringCategory.name_DE === data?.name_DE &&
    scoringCategory.name_LV === data?.name_LV;

  return (
    <Container $paddingY={20}>
      <Box $isNew={true}>
        <Label>{t('Risk category')}</Label>
        <Input name='riskCategory' value={scoringCategory.riskCategory} onChange={onChange} />
        <Space />

        <Label>{t('Name')}</Label>
        <Input name='name' value={scoringCategory.name} onChange={onChange} />
        <Space />

        <Label>{t('Min Points')}</Label>
        <Input
          name='minPoints'
          type='number'
          min={0}
          step={999}
          value={scoringCategory.minPoints}
          onChange={onChangeNumber}
        />
        <Space />

        <Label>{t('Max Points')}</Label>
        <Input
          name='maxPoints'
          type='number'
          min={0}
          step={999}
          value={scoringCategory.maxPoints}
          onChange={onChangeNumber}
        />
        <Space />

        {scoringCategory.id && (
          <>
            <Label>{t('Title')}</Label>
            <Input
              name='title'
              value={scoringCategory.title}
              onChange={onChange}
              placeholder='Title'
            />
            <Space />

            <Label>{`${t('Name')} (RU)`}</Label>
            <Input
              name='name_RU'
              value={scoringCategory.name_RU}
              onChange={onChange}
              placeholder='name_RU'
            />
            <Space />

            <Label>{`${t('Name')} (DE)`}</Label>
            <Input
              name='name_DE'
              value={scoringCategory.name_DE}
              onChange={onChange}
              placeholder='name_DE'
            />
            <Space />

            <Label>{`${t('Name')} (LV)`}</Label>
            <Input
              name='name_LV'
              value={scoringCategory.name_LV}
              onChange={onChange}
              placeholder='name_LV'
            />
            <Space />
          </>
        )}

        <ActionBox>
          <Button onClick={handleAction} disabled={isDisabled} type='primary' loading={isUpdating}>
            {scoringCategory.id ? t('Update Scoring Category') : t('Create Scoring Category')}
          </Button>
          {scoringCategory.id && (
            <Popconfirm
              placement='topRight'
              title={t('Are you sure to delete this scoring category?')}
              onConfirm={onDelete}
              okText={t('Yes')}
              cancelText={t('No')}
            >
              <Button danger loading={isDeleting}>
                {t('Delete Scoring Category')}
              </Button>
            </Popconfirm>
          )}
        </ActionBox>
      </Box>

      {scoringCategory.id && (
        <Container $paddingY={20}>
          <Typography.Title level={4}>{t('Comments')}</Typography.Title>

          {scoringCategory?.comments && (
            <>
              <Collapse accordion>
                {scoringCategory?.comments.map((comment) => (
                  <StyledPanel header={comment.comment} key={comment.id}>
                    <CommentSection sectionProp={comment} />
                  </StyledPanel>
                ))}
              </Collapse>

              <Space />
            </>
          )}

          <NestedBox>
            {showCreateNew ? (
              <CommentSection onCreated={() => setShowCreateNew(false)} />
            ) : (
              <Button onClick={() => setShowCreateNew(true)}>{t('Add new Comment')}</Button>
            )}
          </NestedBox>
        </Container>
      )}
    </Container>
  );
};
